// ============================================================
// TMR Portal Admin — aprovar ou reprovar um pedido de contingência (31 §9)
// ============================================================
import { useState } from 'react';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { Btn } from '../../components/primitives';
import { Icons } from '../../lib/icons';
import type { ContingencyRequest } from '../../data/contingency/contract';
import { formatMoment } from '../../data/contingency/status';

/** Props for {@link DecisionDialog}. */
export interface DecisionDialogProps {
  request: ContingencyRequest;
  outcome: 'approve' | 'reject';
  /** Whether the decision is being sent — the buttons wait, so a double click cannot send it twice. */
  pending: boolean;
  /** The server's refusal, in its own words; null while there is none. */
  error: string | null;
  onConfirm: (note: string) => void;
  onClose: () => void;
}

/**
 * A manager's decision on a request, with the request in front of them — who asked, for which patient and why —
 * and an optional note. Approving issues nothing: the requester generates the code afterwards.
 */
export function DecisionDialog({ request, outcome, pending, error, onConfirm, onClose }: DecisionDialogProps) {
  const [note, setNote] = useState('');
  const approving = outcome === 'approve';

  return (
    <ModalShell
      title={approving ? 'Aprovar o pedido?' : 'Reprovar o pedido?'}
      lead={`${request.requestedBy.name} · ${formatMoment(request.requestedAt)} · paciente ${request.patientCpfMasked}`}
      onClose={pending ? () => undefined : onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-muted)' }}>
          <span className="pa-eyebrow" style={{ display: 'block', marginBottom: 4 }}>Motivo</span>
          <span style={{ color: 'var(--ink-900)', whiteSpace: 'pre-wrap' }}>{request.reason}</span>
        </div>

        <div>
          <label className="pa-form-label" htmlFor="contingency-decision-note">
            Observação {approving ? '(opcional)' : '(recomendada)'}
          </label>
          <textarea
            id="contingency-decision-note"
            className="pa-input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={500}
            disabled={pending}
            placeholder={approving ? 'Algo que quem pediu precisa saber.' : 'Por que reprovar — quem pediu lê isto na lista.'}
            style={{
              width: '100%',
              padding: '9px 12px',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              resize: 'vertical',
            }}
          />
        </div>

        <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-subtle)' }}>
          {approving
            ? 'Aprovar não gera o código: quem pediu gera e passa ao paciente.'
            : 'Reprovar é definitivo. Para outro código, é preciso um pedido novo.'}
        </p>

        {error && <Notice tone="danger">{error}</Notice>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Btn variant="secondary" onClick={onClose} disabled={pending}>
            Cancelar
          </Btn>
          <Btn
            variant="primary"
            icon={approving ? Icons.check : Icons.x}
            style={approving ? undefined : { background: 'var(--danger)', color: 'var(--cream-50)' }}
            disabled={pending}
            onClick={() => onConfirm(note)}
          >
            {pending ? 'Registrando…' : approving ? 'Aprovar' : 'Reprovar'}
          </Btn>
        </div>
      </div>
    </ModalShell>
  );
}
